import { useState } from "react";
import { usePatientJourney, PatientJourneyStep } from "@/hooks/usePatientJourney";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Edit2, Plus, Save, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const ICON_OPTIONS = [
  'Calendar', 'Target', 'Users', 'TrendingUp', 'CheckCircle',
  'Heart', 'Award', 'Activity', 'Star', 'Zap'
];

export function PatientJourneyManager() {
  const { steps, isLoading, updateStep, createStep, deleteStep } = usePatientJourney();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<PatientJourneyStep>>({
    icon: 'Calendar',
    title: '',
    description: '',
    is_active: true,
    display_order: 0
  });

  const handleEdit = (step: PatientJourneyStep) => {
    setEditingId(step.id);
    setFormData(step);
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({
      icon: 'Calendar',
      title: '',
      description: '',
      is_active: true,
      display_order: (steps?.length || 0)
    });
  };

  const handleSave = () => {
    if (!formData.title || !formData.description) {
      return;
    }

    if (isCreating) {
      createStep(formData as Omit<PatientJourneyStep, 'id'>);
      setIsCreating(false);
    } else if (editingId) {
      updateStep({ id: editingId, updates: formData });
      setEditingId(null);
    }
    
    setFormData({
      icon: 'Calendar',
      title: '',
      description: '',
      is_active: true,
      display_order: 0
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsCreating(false);
    setFormData({
      icon: 'Calendar',
      title: '',
      description: '',
      is_active: true,
      display_order: 0
    });
  };

  const handleToggleActive = (step: PatientJourneyStep) => {
    updateStep({ id: step.id, updates: { is_active: !step.is_active } });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Gerenciar Jornada do Paciente</h3>
        <Button onClick={handleCreate} disabled={isCreating || editingId !== null}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Passo
        </Button>
      </div>

      {/* Form de criação/edição */}
      {(isCreating || editingId) && (
        <Card className="border-2 border-primary">
          <CardHeader>
            <CardTitle>{isCreating ? 'Novo Passo' : 'Editar Passo'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Ícone</Label>
              <Select
                value={formData.icon}
                onValueChange={(value) => setFormData({ ...formData, icon: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map((icon) => (
                    <SelectItem key={icon} value={icon}>
                      {icon}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Título (2-100 caracteres)</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                maxLength={100}
                placeholder="Ex: Primeira Consulta"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.title?.length || 0}/100
              </p>
            </div>

            <div>
              <Label>Descrição (10-300 caracteres)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={300}
                rows={3}
                placeholder="Ex: Avaliação completa do seu histórico alimentar"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.description?.length || 0}/300
              </p>
            </div>

            <div>
              <Label>Ordem de Exibição</Label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                min={0}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Ativo</Label>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={!formData.title || !formData.description}>
                <Save className="w-4 h-4 mr-2" />
                Salvar
              </Button>
              <Button onClick={handleCancel} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de passos */}
      <div className="space-y-4">
        {steps?.map((step) => (
          <Card key={step.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {step.icon}
                    </span>
                    <h4 className="font-semibold">{step.title}</h4>
                    {!step.is_active && (
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                        Inativo
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ordem: {step.display_order}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleActive(step)}
                  >
                    <Switch checked={step.is_active} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(step)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      if (confirm('Tem certeza que deseja deletar este passo?')) {
                        deleteStep(step.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}